/**
 * Recovering a lost redline: the steps run in order by `RecoveryChain`.
 *
 *   1. paragraph-id    the paragraphs we last saw, by `uniqueLocalId`
 *   2. neighbour-id    the paragraph next to a remembered neighbour
 *   3. change-ledger   paragraphs Word reported changed since we started watching
 *   4. card-anchor     every anchor the card carries (id, index, heading path, neighbours,
 *                      text, position), checked together against the whole body
 *   5. server          the core resolver, only on Apply or Refind
 *
 * Every candidate must still resemble the text we last anchored to; a tie is
 * reported as ambiguous rather than guessed, and a tie a local step reports is
 * provisional: the server, when the trigger lets it run, may still settle it.
 * Each step declares the triggers it runs for, so the chain, not the step, knows
 * why the server was skipped.
 */
import type { RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import type { AnchorMemory } from "../anchor-memory";
import type { RecoveryBody } from "./recovery-body";
import type { AmbiguousCandidate, AnchorChecks, RecoveryOutcomeKind, RecoveryResult } from "./recovery-result";
import { RecoveryResult as RecoveryResults } from "./recovery-result";

export type RecoveryTier = "paragraph-id" | "neighbour-id" | "change-ledger" | "card-anchor" | "deleted-paragraph" | "server";

/** What caused a reconcile: the first loss, an edit event, an Apply, the periodic heartbeat, or the user asking for Refind. */
export type RecoveryTrigger = "loss" | "event" | "apply" | "heartbeat" | "refind";

export interface RecoveryAttempt {
    attemptedAtMs: number;
    trigger: RecoveryTrigger;
    /** The step that answered, or null when no step ran. */
    tier: RecoveryTier | null;
    outcome: RecoveryOutcomeKind;
    explanation: string | null;
    placement: RedlinePlacement;
    /** The places a conflicted answer named, for the user to choose between; empty for every other outcome. */
    candidates: AmbiguousCandidate[];
    /** What each of the card's five anchors said, when the card-anchor step answered; null when another step did. */
    checks: AnchorChecks | null;
}

export interface RecoveryContext {
    /**
     * The body as the steps read it, taken from Word once for every recovery that runs together
     * (`RecoveryBody`). A step answers every question about the document from it and asks Word
     * nothing.
     */
    body: RecoveryBody;
    memory: AnchorMemory;
    bookmarkNames: string[];
    /** What the redline produces when accepted; the server's LLM reads it to recognise the paragraph. */
    redlineDesiredText: string;
    /**
     * True when a card on these bookmarks writes words back where its paragraphs are gone:
     * it changes a paragraph's text and leaves words in it
     * (`redlineWritesNothingWhereItsParagraphsAreGone` is false). False when every card on
     * them only deletes, or is a comment's or reply's paragraph (its text unchanged), and
     * when no redline is attached.
     *
     * EXAMPLE
     *   an Issue's comment and its remedy rewriting 11.1, the comment attached first -> true
     *   the comment alone                                                             -> false
     */
    cardWritesWhereItsParagraphsAreGone: boolean;
    trigger: RecoveryTrigger;
}

export interface RecoveryStep {
    readonly tier: RecoveryTier;
    /** The triggers this step runs for; "any" for the local steps, which are cheap. */
    readonly triggers: readonly RecoveryTrigger[] | "any";
    recover(context: RecoveryContext): Promise<RecoveryResult>;
}

export interface RecoveryOutcome {
    step: RecoveryStep | null;
    result: RecoveryResult;
    /** A step that was not eligible for the trigger and so ended the chain, for the UI to explain what a user action would do. */
    skippedTier: RecoveryTier | null;
}

/**
 * Runs the steps in order and stops at the first that recovers or is not eligible
 * for the trigger. A tie a step reports stands as the answer unless a later
 * eligible step recovers or reports its own tie.
 */
export class RecoveryChain {
    constructor(private readonly orderedRecoverySteps: RecoveryStep[]) {}

    async recover(context: RecoveryContext): Promise<RecoveryOutcome> {
        let lastAttemptedOutcome: RecoveryOutcome = { step: null, result: RecoveryResults.notFound(null), skippedTier: null };
        /** The tie the latest step reported, which a later step may still settle. */
        let provisionalTie: RecoveryOutcome | null = null;
        for (const recoveryStep of this.orderedRecoverySteps) {
            const stepRunsForEveryTrigger = recoveryStep.triggers === "any";
            const stepRunsForThisTrigger = stepRunsForEveryTrigger || recoveryStep.triggers.includes(context.trigger);
            /**
             * WHEN this tier is not eligible for the current trigger
             *   CONSEQUENCE
             *     Stop the chain at this tier; do not skip ahead. A tie reported before it
             *     is the answer, carrying this tier as the one withheld.
             *
             *   WHY
             *     Tiers are ordered by authority as well as cost.
             *     A later-trigger-only tier blocks everything after it, so the result can report
             *     which tier was withheld by trigger gating.
             *
             *   EXAMPLE
             *     trigger = "heartbeat"
             *     tiers   = paragraph-id:any, neighbour-id:any, server:apply/refind
             *     result  -> skippedTier = "server", not a later tier
             */
            if (!stepRunsForThisTrigger) {
                if (provisionalTie !== null) return { ...provisionalTie, skippedTier: recoveryStep.tier };
                return { step: null, result: RecoveryResults.notFound(null), skippedTier: recoveryStep.tier };
            }
            const recoveryResult = await recoveryStep.recover(context);
            /**
             * CASES
             *   - outcome = "recovered"  -> return this tier's anchor
             *   - outcome = "ambiguous"  -> hold this tier's candidates and keep scanning: a later
             *                               tier (the server, on a user's trigger) may settle the
             *                               tie, and its own tie replaces this one
             *   - outcome = "not_found"  -> keep scanning later tiers
             *
             * WHY
             *   A local tie names the places that fit and which anchors disagreed, which is
             *   the right answer when nothing more can run; it must not keep the server
             *   from running when the trigger allows it: the server runs only when none
             *   of the card's anchors settles it.
             */
            if (recoveryResult.outcome === "recovered") return { step: recoveryStep, result: recoveryResult, skippedTier: null };
            if (recoveryResult.outcome === "ambiguous") {
                provisionalTie = { step: recoveryStep, result: recoveryResult, skippedTier: null };
                continue;
            }
            lastAttemptedOutcome = { step: recoveryStep, result: recoveryResult, skippedTier: null };
        }
        return provisionalTie ?? lastAttemptedOutcome;
    }
}
